import type { DronePadConfig } from "./drone-pad";
import type { CloudReverbParams } from "./cloud-reverb-node";

export interface DronePreset {
  label: string;
  description: string;
  pad: DronePadConfig;
  reverb: Partial<CloudReverbParams>;
}

export type DronePresetName = keyof typeof DRONE_PRESETS;

/**
 * Thick, analog-style warmth. Rich sawtooth harmonics filtered down to a
 * mellow glow with a sub-oscillator for low-end weight. Sits underneath
 * everything without drawing attention.
 *
 * Best for: general worship, singing, background fill.
 */
const WARM: DronePreset = {
  label: "Warm",
  description: "Analog foundation — thick, mellow, unobtrusive",
  pad: {
    oscillators: [
      { waveform: "sawtooth", ratio: 1, detune: -8, gain: 1 / 3, pan: -0.8 },
      { waveform: "sawtooth", ratio: 1, detune: 0, gain: 1 / 3, pan: 0 },
      { waveform: "sawtooth", ratio: 1, detune: 8, gain: 1 / 3, pan: 0.8 },
    ],
    subOscGain: 0.15,
    noiseGain: 0,
    noiseBandwidth: [200, 2000],
    filterCutoff: 1000,
    filterHPCutoff: 40,
    filterQ: 0.7,
    filterLfoRate: 0.08,
    filterLfoDepth: 0.25,
    ampLfoRate: 0,
    ampLfoDepth: 0,
    driftDepth: 3,
    driftRate: 0.15,
    attack: 3.5,
    release: 5,
    volume: -12,
  },
  reverb: {
    cloudDecay: 10,
    cloudPreDelay: 0.05,
    cloudDamping: 0.35,
    cloudModDepth: 0.4,
    cloudMix: 0.55,
    shimmerMix: 0.1,
    shimmerAmount: 0.05,
  },
};

/**
 * Pure, transparent, glass-like. Clean sine/triangle partials at harmonic
 * intervals (fundamental + octave + twelfth) — like a distant pipe organ
 * or a sustained choir vowel.
 *
 * Best for: prayer moments, altar calls, transitions.
 */
const CELESTIAL: DronePreset = {
  label: "Celestial",
  description: "Glassy organ harmonics — pure, transparent, heavenly",
  pad: {
    oscillators: [
      { waveform: "sine", ratio: 1, detune: -2, gain: 0.35, pan: -0.7 },
      { waveform: "sine", ratio: 1, detune: 2, gain: 0.35, pan: 0.7 },
      { waveform: "sine", ratio: 2, detune: -1, gain: 0.18, pan: -0.35 },
      { waveform: "sine", ratio: 2, detune: 1, gain: 0.18, pan: 0.35 },
      { waveform: "triangle", ratio: 3, detune: 0, gain: 0.1, pan: 0 },
    ],
    subOscGain: 0,
    noiseGain: 0,
    noiseBandwidth: [200, 2000],
    filterCutoff: 4000,
    filterHPCutoff: 80,
    filterQ: 0.5,
    filterLfoRate: 0.04,
    filterLfoDepth: 0.15,
    ampLfoRate: 0,
    ampLfoDepth: 0,
    // Pure tones expose pitch motion more than saws, so the drift is gentler.
    driftDepth: 1.5,
    driftRate: 0.12,
    attack: 5,
    release: 8,
    volume: -14,
  },
  reverb: {
    cloudDecay: 10,
    cloudPreDelay: 0.06,
    cloudDamping: 0.25,
    cloudModDepth: 0.5,
    cloudMix: 0.5,
    shimmerDecay: 8,
    shimmerMix: 0.45,
    shimmerAmount: 0.35,
    shimmerPitch1: 12,
    shimmerPitch2: 24,
    shimmerBalance: 0.3,
  },
};

/**
 * Wide, rich ensemble created by many slightly-detuned sawtooth oscillators
 * (classic string-machine technique). The natural chorus of 5 voices provides
 * spatial width.
 *
 * No filter LFO: its 0.2 Hz brightness sweep read as the pad "moving"
 * (measured as the dominant slow spectral change). The wide ±20-cent detune
 * is kept deliberately — its 3 Hz flutter reads as static texture, whereas
 * narrower detunes beat slowly and reintroduce swells.
 *
 * Best for: building moments, dynamic worship, anthem sections.
 */
const STRINGS: DronePreset = {
  label: "Strings",
  description: "Ensemble chorus — wide, rich, steady",
  pad: {
    oscillators: [
      { waveform: "sawtooth", ratio: 1, detune: -20, gain: 0.2, pan: -0.8 },
      { waveform: "sawtooth", ratio: 1, detune: -7, gain: 0.2, pan: 0.4 },
      { waveform: "sawtooth", ratio: 1, detune: 0, gain: 0.2, pan: 0 },
      { waveform: "sawtooth", ratio: 1, detune: 7, gain: 0.2, pan: -0.4 },
      { waveform: "sawtooth", ratio: 1, detune: 20, gain: 0.2, pan: 0.8 },
    ],
    subOscGain: 0,
    noiseGain: 0,
    noiseBandwidth: [200, 2000],
    filterCutoff: 1800,
    filterHPCutoff: 60,
    filterQ: 0.8,
    filterLfoRate: 0.2,
    filterLfoDepth: 0,
    ampLfoRate: 0,
    ampLfoDepth: 0,
    driftDepth: 3,
    driftRate: 0.15,
    attack: 2.5,
    release: 4,
    volume: -14,
  },
  reverb: {
    cloudDecay: 6,
    cloudPreDelay: 0.04,
    cloudDamping: 0.3,
    cloudModDepth: 0.25,
    cloudMix: 0.45,
    shimmerMix: 0.15,
    shimmerAmount: 0.1,
  },
};

/**
 * Minimal, dark, meditative. Sine-based with very few harmonics, a prominent
 * sub-octave for physical warmth, filtered noise for a "breath" quality, and
 * a slow amplitude LFO that creates living, breathing swells.
 *
 * Best for: quiet prayer, communion, contemplation, scripture reading.
 */
const PRAYER: DronePreset = {
  label: "Prayer",
  description: "Deep ambient breath — dark, minimal, meditative",
  pad: {
    oscillators: [
      { waveform: "sine", ratio: 1, detune: -5, gain: 0.3, pan: -0.4 },
      { waveform: "sine", ratio: 1, detune: 5, gain: 0.3, pan: 0.4 },
      { waveform: "triangle", ratio: 1, detune: 0, gain: 0.15, pan: 0 },
    ],
    subOscGain: 0.15,
    noiseGain: 0.03,
    noiseBandwidth: [100, 800],
    filterCutoff: 600,
    filterHPCutoff: 30,
    filterQ: 0.5,
    filterLfoRate: 0.03,
    filterLfoDepth: 0.2,
    ampLfoRate: 0.06,
    ampLfoDepth: 0.15,
    driftDepth: 1.5,
    driftRate: 0.1,
    attack: 6,
    release: 10,
    volume: -16,
  },
  reverb: {
    cloudDecay: 12,
    cloudPreDelay: 0.08,
    cloudDamping: 0.5,
    cloudModDepth: 0.55,
    cloudMix: 0.5,
    shimmerMix: 0,
    shimmerAmount: 0,
  },
};

// Insertion order is the preset-picker display order.
export const DRONE_PRESETS = {
  celestial: CELESTIAL,
  warm: WARM,
  strings: STRINGS,
  prayer: PRAYER,
} as const satisfies Record<string, DronePreset>;
